import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Download,
  Eye,
  Pencil,
  Plus,
  Power,
  Printer,
  RefreshCw,
  X,
} from "lucide-react";
import QRCode from "qrcode";
import JSZip from "jszip";
import { toast } from "sonner";
import api from "../../lib/api";
import { usePos } from "../../context/PosContext";

const emptyForm = {
  code: "",
  name: "",
};

export default function TablesPage() {
  const { outlets = [], outletId } = usePos();

  const [selectedOutletId, setSelectedOutletId] = useState(outletId || "");
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const [qrPreviewTable, setQrPreviewTable] = useState(null);
  const [qrPreviewUrl, setQrPreviewUrl] = useState("");
  const [qrBusy, setQrBusy] = useState(false);

  useEffect(() => {
    if (!selectedOutletId && outletId) {
      setSelectedOutletId(outletId);
    }
  }, [outletId, selectedOutletId]);

  const selectedOutlet = useMemo(
    () => outlets.find((outlet) => outlet.id === selectedOutletId),
    [outlets, selectedOutletId]
  );

  const loadTables = useCallback(async () => {
    if (!selectedOutletId) {
      setTables([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await api.get("/tables", {
        params: { outlet_id: selectedOutletId },
      });

      setTables(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const message =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail ||
        "Failed to load tables.";

      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [selectedOutletId]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  useEffect(() => {
    const validTableIds = new Set(tables.map((table) => table.id));

    setSelectedTableIds((current) =>
      current.filter((id) => validTableIds.has(id))
    );
  }, [tables]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (table) => {
    setEditing(table);
    setForm({
      code: table.code || "",
      name: table.name || "",
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const code = form.code.trim();
    const name = form.name.trim();

    if (!selectedOutletId) {
      toast.error("Please select an outlet first.");
      return;
    }

    if (!code || !name) {
      toast.error("Code dan nama meja wajib diisi.");
      return;
    }

    setSaving(true);

    try {
      if (editing) {
        await api.patch(`/tables/${editing.id}`, {
          code,
          name,
        });

        toast.success("Table updated successfully.");
      } else {
        await api.post("/tables", {
          outlet_id: selectedOutletId,
          code,
          name,
        });

        toast.success("Table added successfully.");
      }

      closeForm();
      await loadTables();
    } catch (err) {
      const message =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail ||
        "Failed to save table.";

      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const escapeHtml = useCallback((value) => {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }, []);

  const getSelfOrderUrl = useCallback((table) => {
    if (!table?.public_token) return "";
    return `${window.location.origin}/self-order/${encodeURIComponent(
      table.public_token
    )}`;
  }, []);

  const generateQrDataUrl = useCallback(async (table) => {
    const url = getSelfOrderUrl(table);

    if (!url) {
      throw new Error("QR Self Order tidak tersedia untuk meja ini.");
    }

    return QRCode.toDataURL(url, {
      width: 1200,
      margin: 3,
      errorCorrectionLevel: "M",
    });
  }, [getSelfOrderUrl]);

  const activeTables = useMemo(
    () => tables.filter((table) => table.active && table.public_token),
    [tables]
  );

  const selectedActiveTables = useMemo(
    () =>
      activeTables.filter((table) =>
        selectedTableIds.includes(table.id)
      ),
    [activeTables, selectedTableIds]
  );

  const allActiveSelected =
    activeTables.length > 0 &&
    activeTables.every((table) => selectedTableIds.includes(table.id));

  const toggleTableSelection = (tableId) => {
    setSelectedTableIds((current) =>
      current.includes(tableId)
        ? current.filter((id) => id !== tableId)
        : [...current, tableId]
    );
  };

  const toggleSelectAllActive = () => {
    if (allActiveSelected) {
      setSelectedTableIds([]);
      return;
    }

    setSelectedTableIds(activeTables.map((table) => table.id));
  };

  const openQrPreview = async (table) => {
    if (!table?.active || !table?.public_token) return;

    try {
      setQrBusy(true);
      const dataUrl = await generateQrDataUrl(table);
      setQrPreviewTable(table);
      setQrPreviewUrl(dataUrl);
    } catch (err) {
      toast.error(err?.message || "Gagal membuat QR Code.");
    } finally {
      setQrBusy(false);
    }
  };

  const closeQrPreview = () => {
    if (qrBusy) return;
    setQrPreviewTable(null);
    setQrPreviewUrl("");
  };

  const downloadQr = async (table) => {
    if (!table?.active || !table?.public_token) return;

    try {
      setQrBusy(true);

      const dataUrl = await generateQrDataUrl(table);
      const link = document.createElement("a");

      link.href = dataUrl;
      link.download = `self-order-${table.code || table.id}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`QR ${table.name || table.code} berhasil diunduh.`);
    } catch (err) {
      toast.error(err?.message || "Gagal mengunduh QR Code.");
    } finally {
      setQrBusy(false);
    }
  };

  const printQrTables = async (tableList) => {
    const printableTables = tableList.filter(
      (table) => table?.active && table?.public_token
    );

    if (!printableTables.length) {
      toast.error("Tidak ada table aktif yang memiliki QR Self Order.");
      return;
    }

    try {
      setQrBusy(true);

      const qrItems = await Promise.all(
        printableTables.map(async (table) => ({
          table,
          dataUrl: await generateQrDataUrl(table),
          url: getSelfOrderUrl(table),
        }))
      );

      const printWindow = window.open("", "_blank", "width=900,height=700");

      if (!printWindow) {
        toast.error("Popup print diblokir browser. Izinkan popup untuk GLOO POS.");
        return;
      }

      const cards = qrItems
        .map(
          ({ table, dataUrl, url }) => `
            <div class="qr-card">
              <div class="eyebrow">SELF ORDER</div>
              <div class="table-name">${escapeHtml(table.name || "-")}</div>
              <div class="table-code">${escapeHtml(table.code || "-")}</div>
              <img src="${dataUrl}" alt="QR Self Order ${escapeHtml(table.name || table.code || "-")}" />
              <div class="instruction">Scan untuk memesan</div>
              <div class="url">${escapeHtml(url)}</div>
            </div>
          `
        )
        .join("");

      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>GLOO POS - Self Order QR</title>
            <style>
              @page {
                size: A4;
                margin: 10mm;
              }

              * {
                box-sizing: border-box;
              }

              body {
                margin: 0;
                font-family: Arial, Helvetica, sans-serif;
                color: #111827;
                background: white;
              }

              .sheet {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 8mm;
              }

              .qr-card {
                min-height: 125mm;
                border: 1px solid #d1d5db;
                border-radius: 6mm;
                padding: 8mm;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                break-inside: avoid;
              }

              .eyebrow {
                font-size: 10pt;
                font-weight: 700;
                letter-spacing: 1.5px;
                margin-bottom: 3mm;
              }

              .table-name {
                font-size: 20pt;
                font-weight: 700;
                margin-bottom: 1mm;
              }

              .table-code {
                font-size: 10pt;
                color: #6b7280;
                margin-bottom: 6mm;
              }

              img {
                width: 62mm;
                height: 62mm;
                display: block;
              }

              .instruction {
                margin-top: 6mm;
                font-size: 12pt;
                font-weight: 600;
              }

              .url {
                margin-top: 3mm;
                max-width: 80mm;
                font-size: 7pt;
                color: #6b7280;
                word-break: break-all;
              }

              @media print {
                .qr-card {
                  box-shadow: none;
                }
              }
            </style>
          </head>
          <body>
            <div class="sheet">
              ${cards}
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();

      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } catch (err) {
      toast.error(err?.message || "Gagal menyiapkan halaman print.");
    } finally {
      setQrBusy(false);
    }
  };

  const downloadQrZip = async (tableList) => {
    const downloadableTables = tableList.filter(
      (table) => table?.active && table?.public_token
    );

    if (!downloadableTables.length) {
      toast.error("Tidak ada table aktif yang memiliki QR Self Order.");
      return;
    }

    try {
      setQrBusy(true);

      const zip = new JSZip();

      for (const table of downloadableTables) {
        const dataUrl = await generateQrDataUrl(table);
        const base64 = dataUrl.split(",")[1];

        zip.file(
          `self-order-${table.code || table.id}.png`,
          base64,
          { base64: true }
        );
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `gloo-self-order-qr-${selectedOutlet?.code || "outlet"}.zip`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);

      toast.success(
        `${downloadableTables.length} QR berhasil dikemas dalam ZIP.`
      );
    } catch (err) {
      toast.error(err?.message || "Gagal membuat file ZIP QR.");
    } finally {
      setQrBusy(false);
    }
  };

  const toggleStatus = async (table) => {
    try {
      await api.patch(`/tables/${table.id}/status`, {
        active: !table.active,
      });

      toast.success(
        table.active ? "Table deactivated." : "Table activated."
      );

      await loadTables();
    } catch (err) {
      const message =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail ||
        "Failed to change table status.";

      toast.error(message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tables</h1>
          <p className="text-sm text-muted-foreground">
            Manage outlet tables for Self Order.
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            value={selectedOutletId}
            onChange={(event) => setSelectedOutletId(event.target.value)}
            className="h-10 rounded-md border bg-background px-3 text-sm"
            disabled={!outlets.length}
          >
            {!outlets.length && <option value="">No outlets available</option>}

            {outlets.map((outlet) => (
              <option key={outlet.id} value={outlet.id}>
                {outlet.name}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={openCreate}
            disabled={!selectedOutletId}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Add Table
          </button>

          <button
            type="button"
            onClick={loadTables}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {selectedOutlet && (
        <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm">
          Active outlet:{" "}
          <span className="font-medium">{selectedOutlet.name}</span>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="rounded-xl border bg-card">
        <div className="border-b px-5 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-semibold">Table List</h2>
              <p className="text-sm text-muted-foreground">
                {tables.length} table(s) found at this outlet.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {selectedActiveTables.length} selected
              </span>

              <button
                type="button"
                onClick={() => downloadQrZip(selectedActiveTables)}
                disabled={qrBusy || selectedActiveTables.length === 0}
                className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5" />
                Download Selected
              </button>

              <button
                type="button"
                onClick={() => printQrTables(selectedActiveTables)}
                disabled={qrBusy || selectedActiveTables.length === 0}
                className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Printer className="h-3.5 w-3.5" />
                Print Selected
              </button>

              <button
                type="button"
                onClick={() => downloadQrZip(activeTables)}
                disabled={qrBusy || activeTables.length === 0}
                className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5" />
                Download All
              </button>

              <button
                type="button"
                onClick={() => printQrTables(activeTables)}
                disabled={qrBusy || activeTables.length === 0}
                className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Printer className="h-3.5 w-3.5" />
                Print All
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">
            Loading tables...
          </div>
        ) : tables.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="font-medium">No tables yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Add the first table for this outlet.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-left">
                  <th className="w-12 px-5 py-3">
                    <input
                      type="checkbox"
                      checked={allActiveSelected}
                      onChange={toggleSelectAllActive}
                      disabled={activeTables.length === 0}
                      aria-label="Select all active tables"
                      className="h-4 w-4 rounded border"
                    />
                  </th>
                  <th className="px-5 py-3 font-medium">Code</th>
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">QR Self Order</th>
                  <th className="px-5 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>

              <tbody>
                {tables.map((table) => {
                  const qrAvailable = Boolean(
                    table.active && table.public_token
                  );
                  const selected = selectedTableIds.includes(table.id);

                  return (
                    <tr key={table.id} className="border-b last:border-0">
                      <td className="px-5 py-4">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleTableSelection(table.id)}
                          disabled={!qrAvailable}
                          aria-label={`Select ${table.name || table.code}`}
                          className="h-4 w-4 rounded border"
                        />
                      </td>

                      <td className="px-5 py-4 font-medium">
                        {table.code}
                      </td>

                      <td className="px-5 py-4">{table.name}</td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                            table.active
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {table.active ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => openQrPreview(table)}
                            disabled={!qrAvailable || qrBusy}
                            className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View QR
                          </button>

                          <button
                            type="button"
                            onClick={() => downloadQr(table)}
                            disabled={!qrAvailable || qrBusy}
                            className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Download
                          </button>

                          <button
                            type="button"
                            onClick={() => printQrTables([table])}
                            disabled={!qrAvailable || qrBusy}
                            className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Printer className="h-3.5 w-3.5" />
                            Print
                          </button>
                        </div>

                        {!table.public_token && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            QR belum tersedia
                          </p>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(table)}
                            className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => toggleStatus(table)}
                            className="inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs font-medium"
                          >
                            <Power className="h-3.5 w-3.5" />
                            {table.active ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h2 className="font-semibold">
                  {editing ? "Edit Table" : "Add Table"}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {selectedOutlet?.name || "Outlet"}
                </p>
              </div>

              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                className="rounded-md p-2 hover:bg-muted disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 p-5">
              <div className="space-y-2">
                <label className="text-sm font-medium">Code Meja</label>
                <input
                  value={form.code}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      code: event.target.value,
                    }))
                  }
                  placeholder="Example: T01"
                  maxLength={50}
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  disabled={saving}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Name Meja</label>
                <input
                  value={form.name}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  placeholder="Example: Table 01"
                  maxLength={100}
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  disabled={saving}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="h-10 rounded-md border px-4 text-sm font-medium"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {qrPreviewTable && qrPreviewUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h2 className="font-semibold">QR Self Order</h2>
                <p className="text-xs text-muted-foreground">
                  {qrPreviewTable.name || qrPreviewTable.code}
                </p>
              </div>

              <button
                type="button"
                onClick={closeQrPreview}
                disabled={qrBusy}
                className="rounded-md p-2 hover:bg-muted disabled:opacity-50"
                aria-label="Close QR preview"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="rounded-xl border bg-white p-4">
                <img
                  src={qrPreviewUrl}
                  alt={`QR Self Order ${qrPreviewTable.name || qrPreviewTable.code}`}
                  className="mx-auto h-72 w-72 max-w-full object-contain"
                />
              </div>

              <div className="text-center">
                <p className="font-semibold">
                  {qrPreviewTable.name || "-"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {qrPreviewTable.code || "-"}
                </p>
                <p className="mt-2 break-all text-xs text-muted-foreground">
                  {getSelfOrderUrl(qrPreviewTable)}
                </p>
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => downloadQr(qrPreviewTable)}
                  disabled={qrBusy}
                  className="inline-flex h-10 items-center gap-2 rounded-md border px-4 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  Download PNG
                </button>

                <button
                  type="button"
                  onClick={() => printQrTables([qrPreviewTable])}
                  disabled={qrBusy}
                  className="inline-flex h-10 items-center gap-2 rounded-md border px-4 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Printer className="h-4 w-4" />
                  Print
                </button>

                <button
                  type="button"
                  onClick={closeQrPreview}
                  disabled={qrBusy}
                  className="inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
